import axios from 'axios';

// API base URL pointing to Rails backend server
const API_BASE_URL = (window.location.hostname === "localhost") ? "http://localhost:3000" : process.env.REACT_APP_API_BASE_URL


const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests if available
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle token expiration
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  login: (identifier, password) => 
    api.post('/auth/login', { identifier, password }),
  
  register: (userData) => 
    api.post('/auth/register', { user: userData }),

  forgotPassword: (identifier) =>
    api.post('/auth/forgot_password', { identifier }),

  resetPassword: (token, password, passwordConfirmation) =>
    api.post('/auth/reset_password', { 
      token, 
      password, 
      password_confirmation: passwordConfirmation 
    }),
};

export const usersAPI = {
  getAll: () => api.get('/users'),
  getById: (id) => api.get(`/users/${id}`),
  update: (id, userData) => {
    // Handle file uploads with FormData
    if (userData.photo) {
      const formData = new FormData();
      Object.keys(userData).forEach(key => {
        if (key === 'photo') {
          formData.append('user[photo]', userData[key]);
        } else {
          formData.append(`user[${key}]`, userData[key]);
        }
      });
      
      return api.put(`/users/${id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    } else {
      return api.put(`/users/${id}`, { user: userData });
    }
  },
  delete: (id) => api.delete(`/users/${id}`),
};

export const productsAPI = {
  getAll: (params = {}) => api.get('/products', { params }),
  getById: (id) => api.get(`/products/${id}`),
};

export const cartItemsAPI = {
  getAll: () => api.get('/cart_items'),
  add: (productId, quantity = 1) => api.post('/cart_items', { cart_item: { product_id: productId, quantity } }),
  update: (id, quantity) => api.put(`/cart_items/${id}`, { cart_item: { quantity } }),
  remove: (id) => api.delete(`/cart_items/${id}`),
  clear: () => api.delete('/cart_items/clear'),
  sync: (cartItems) => api.post('/cart_items/sync', { cart_items: cartItems }),
};

export const ordersAPI = {
  getAll: () => api.get('/orders'),
  getById: (id) => api.get(`/orders/${id}`),
  create: (orderData = {}) => api.post('/orders', orderData),
  update: (id, orderData) => api.put(`/orders/${id}`, { order: orderData }),
  reorder: (id) => api.post(`/orders/${id}/reorder`),
  track: (id) => api.get(`/orders/${id}/track`),
  initiatePayment: (id) => api.post(`/orders/${id}/initiate_payment`),
  confirmPayment: (id) => api.post(`/orders/${id}/confirm_payment`),
};

export const notificationsAPI = {
  getAll: () => api.get('/notifications'),
  getById: (id) => api.get(`/notifications/${id}`),
  markAsRead: (id) => api.patch(`/notifications/${id}/mark_as_read`),
  markAllAsRead: () => api.patch('/notifications/mark_all_as_read'),
};

export const wishlistAPI = {
  getAll: () => api.get('/wishlist_items'),
  add: (productId) => api.post('/wishlist_items', { wishlist_item: { product_id: productId } }),
  remove: (id) => api.delete(`/wishlist_items/${id}`),
  clear: () => api.delete('/wishlist_items/clear'),
};

export const ratingsAPI = {
  rate: (productId, rating) => api.post('/product_ratings', { product_rating: { product_id: productId, rating } }),
};

export const adminOrdersAPI = {
  getAll: (params = {}) => api.get('/admin/orders', { params }),
  getById: (id) => api.get(`/admin/orders/${id}`),
  update: (id, orderData) => api.put(`/admin/orders/${id}`, { order: orderData }),
};

export const adminProductsAPI = {
  getAll: (params = {}) => api.get('/admin/products', { params }),
  getById: (id) => api.get(`/admin/products/${id}`),
  create: (productData) => {
    const formData = new FormData();
    Object.entries(productData).forEach(([key, value]) => {
      if (key === 'image') {
        if (value) {
          formData.append('product[image]', value);
        }
      } else {
        formData.append(`product[${key}]`, value);
      }
    });
    return api.post('/admin/products', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  update: (id, productData) => {
    const formData = new FormData();
    Object.entries(productData).forEach(([key, value]) => {
      if (key === 'image') {
        if (value) {
          formData.append('product[image]', value);
        }
      } else {
        formData.append(`product[${key}]`, value);
      }
    });
    return api.put(`/admin/products/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  delete: (id) => api.delete(`/admin/products/${id}`),
  getStockReport: () => api.get('/admin/products/stock_report'),
  getLowStock: () => api.get('/admin/products/low_stock'),
  getOutOfStock: () => api.get('/admin/products/out_of_stock'),
};

export const appVersionsAPI = {
  getLatest: () => api.get('/app_versions/latest'),
  download: (id) => api.get(`/app_versions/${id}/download`, { responseType: 'blob' }),
};

export const adminAppVersionsAPI = {
  getAll: () => api.get('/admin/app_versions'),
  getById: (id) => api.get(`/admin/app_versions/${id}`),
  create: (versionData) => {
    const formData = new FormData();
    Object.keys(versionData).forEach(key => {
      if (key === 'apk_file') {
        if (versionData[key]) {
          formData.append('app_version[apk_file]', versionData[key]);
        }
      } else {
        formData.append(`app_version[${key}]`, versionData[key]);
      }
    });
    return api.post('/admin/app_versions', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  update: (id, versionData) => {
    const formData = new FormData();
    Object.keys(versionData).forEach(key => {
      if (key === 'apk_file') {
        if (versionData[key]) {
          formData.append('app_version[apk_file]', versionData[key]);
        }
      } else {
        formData.append(`app_version[${key}]`, versionData[key]);
      }
    });
    return api.put(`/admin/app_versions/${id}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  delete: (id) => api.delete(`/admin/app_versions/${id}`),
  activate: (id) => api.post(`/admin/app_versions/${id}/activate`),
};

export default api;
